import React, { useState, useCallback, useEffect } from 'react';
import { Route, Routes, Navigate, useLocation, useNavigate, Outlet, Link } from 'react-router-dom';
import PlaygroundTab from '../PlaygroundTab/PlaygroundTab';
import SearchTab from '../SearchTab/SearchTab';
import ActivityTab from '../ActivityTab/ActivityTab';
import ProfileTab from '../ProfileTab/ProfileTab';
import MessagesTab from '../MessagesTab/MessagesTab';
import BottomTabBar from '../../BottomTabBar/BototmTabBar';
import NewPostInputModal from '../PlaygroundTab/Post/NewPostInputModal';
import LoadingScreen from '../../common/LoadingScreen/LoadingScreen';
import { fetchUserData } from '../../../api/auth';
import { UserType } from '../../../types/userType';
import { fetchRecommendedKeywords } from '../../../api/searchServices';
import './HomeTabs.css';

const HomeTabs: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserType | null>(null);
  const [previousPath, setPreviousPath] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReferralBanner, setShowReferralBanner] = useState(true);

  const isPostDetailsView = location.pathname.includes('/posts/');

  // Store the previous path when navigating
  useEffect(() => {
    if (!isPostDetailsView) {
      setPreviousPath(location.pathname);
    }
  }, [location, isPostDetailsView]);

  const openNewPostModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeNewPostModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const dismissReferralBanner = useCallback(() => {
    setShowReferralBanner(false);
    // Store in localStorage so it stays dismissed for 3 days
    localStorage.setItem('referralBannerDismissed', new Date().toISOString());
  }, []);

  // Check if the banner should be shown (not shown for 3 days after dismissal)
  useEffect(() => {
    const lastDismissed = localStorage.getItem('referralBannerDismissed');
    if (lastDismissed) {
      const dismissedDate = new Date(lastDismissed);
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
      
      if (dismissedDate > threeDaysAgo) {
        setShowReferralBanner(false);
      }
    }
  }, []);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        setIsLoading(true);
        const userData = await fetchUserData();
        setCurrentUser(userData);
        localStorage.setItem('viUser', JSON.stringify(userData));

        // Fetch and store recommended keywords
        const keywords = await fetchRecommendedKeywords();
        localStorage.setItem('recommendedKeywords', JSON.stringify(keywords));
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, []);

  // if (isLoading) {
  //   return <LoadingScreen />;
  // }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  const renderActiveTab = () => {
    if (isPostDetailsView) return null;
    
    switch (location.pathname) {
      case '/playground':
        return (
          <PlaygroundTab 
            onOpenModal={openNewPostModal}
            key="playground"
            isActive={true}
            currentUser={currentUser}
          />
        );
      case '/search':
        return (
          <SearchTab 
            key="search" 
            isActive={true} 
            currentUser={currentUser}
          />
        );
      case '/activity':
        return (
          <ActivityTab 
            key="activity" 
            isActive={true} 
          />
        );
      case '/profile':
        return (
          <ProfileTab 
            key="profile" 
            isActive={true} 
            currentUser={currentUser}
          />
        );
      // case '/messages':
      //   return (
      //     <MessagesTab 
      //       key="messages"
      //       isActive={true}
      //     />
      //   );
      default:
        return null;
    }
  };

  const handleReferralClick = () => {
    navigate('/add-referrals');
  };

  return (
    <>
      <div className="home-tabs">
        {showReferralBanner && (
          <div className="referral-banner">
            <div className="referral-banner-content">
              <p>
                <strong>🎁 Referral Rewards:</strong> Tell us who referred you to collabs and help them earn rewards!
              </p>
              <div className="referral-banner-actions">
                <button className="referral-banner-button" onClick={handleReferralClick}>
                  Add Referrals
                </button>
                <button className="referral-banner-dismiss" onClick={dismissReferralBanner}>
                  ×
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="content">
          {renderActiveTab()}
          <Outlet context={{ currentUser }} />
        </div>
        {!isPostDetailsView && <BottomTabBar />}
      </div>
      <NewPostInputModal
        isOpen={isModalOpen}
        onClose={closeNewPostModal}
        username={currentUser?.username || ''}
        avatarSrc={currentUser?.avatar|| ''}
        currentUser={currentUser!}
      />
    </>
  );
};

export default HomeTabs;
